import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Grid, Paper, useMediaQuery } from '@mui/material';
import Titlebar from '../TitleBar';
import { Pagination, SearchTable, TableBody2, TableBottom, TableHeader } from '.';
import Table from '@mui/material/Table';
import TableContainer from '@mui/material/TableContainer';
import { applyFilter, emptyRows, getComparator, extractDate } from '../../Utils/table';
import { AdminSaleMenu, adminSaleReport, StkInwMenu } from '../TitleList';
import { selectAdminSaleData, selectLocations, selectZones } from '../Selectors/Master/OutwardSelectors';
import { fetchAdminSaleData, fetchAdminSaleReport } from '../../Store/PHPSlice/stockApi';
import DateInputComponent2 from '../FieldsComponents/DateInputComponent2';
import styled, { inputStyles } from '../../Utils/inputChanges';
import Dropdown from '../FieldsComponents/Dropdown';
import { fetchZoneData } from '../../Store/PHPSlice/zone';
import { fetchLocationData } from '../../Store/PHPSlice/LocationApi';


const AdminSaleReport = () => {
    const dispatch = useDispatch();
    const getItems = useSelector(selectAdminSaleData);
    const zones = useSelector(selectZones);
    const schools = useSelector(selectLocations);

    const isDesktop = useMediaQuery('(min-width:768px)');

    const [order, setOrder] = useState('asc');
    const [orderBy, setOrderBy] = useState('empId');
    const [selected, setSelected] = useState([]);
    const [filterName, setFilterName] = useState('null');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);
    const [recordsPerPage, setRecordsPerPage] = useState(10);
    const [searchQuery, setSearchQuery] = useState("");
    const [firstIndex, setFirstIndex] = useState(0);
    const [lastIndex, setLastIndex] = useState(0);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [pageRange, setPageRange] = useState([0, 10]); // Initial range [0, 10]
    const [searchTerm, setSearchTerm] = useState('');

    const [formData, setFormData] = useState({
        fdt: '',
        tdt: '',
        zoneId: '',
        schlId: ''
    });

    const [reportData, setReportData] = useState([]);

    useEffect(() => {
        dispatch(fetchAdminSaleData());
        dispatch(fetchZoneData());
        dispatch(fetchLocationData());
    }, [dispatch]);

    if (!getItems) {
        return <h1>Loading...</h1>;
    }
    // console.log(reportData)
    const compData = Array.isArray(reportData.info) ? reportData?.info?.map((item, index) => (
        {
            srNo: item.ID,
            dt: item.dt,
            billNo: item.billNo,
            zoneId: item.zoneId,
            zoneNm: item.zoneNm,
            schlId: item.schlId,
            schlNm: item.schlNm,
            schlcd: item.schlcd,
            stkId: item.stkId,
            igst: item.igst,
            cgst: item.cgst,
            sgst: item.sgst,
            totissqty: item.totissqty,
            totamt: item.totamt,
            totsqty: item.totsqty,
            totbqty: item.totbqty,
            sts: item.sts,
            serialNumber: index + 1
        }
    )) : [];


    const handleSort = (event, id) => {
        const isAsc = orderBy === id && order === 'asc';
        if (id !== '') {
            setOrder(isAsc ? 'desc' : 'asc');
            setOrderBy(id);
        }
    };

    const handleSelectAllClick = (event) => {
        if (event.target.checked) {
            const newSelecteds = compData.map((n) => n.name);
            setSelected(newSelecteds);
            return;
        }
        setSelected([]);
    };

    const handleClick = (event, name) => {
        const selectedIndex = selected.indexOf(name);
        let newSelected = [];
        if (selectedIndex === -1) {
            newSelected = newSelected.concat(selected, name);
        } else if (selectedIndex === 0) {
            newSelected = newSelected.concat(selected.slice(1));
        } else if (selectedIndex === selected.length - 1) {
            newSelected = newSelected.concat(selected.slice(0, -1));
        } else if (selectedIndex > 0) {
            newSelected = newSelected.concat(
                selected.slice(0, selectedIndex),
                selected.slice(selectedIndex + 1)
            );
        }
        setSelected(newSelected);
    };


    const totalCount = compData.length;
    const npages = Math.ceil(totalCount / recordsPerPage);
    const numbers = Array.from({ length: npages }, (_, i) => i + 1);

    const handleSearchChange = (event) => {
        setSearchTerm(event.target.value);
        setCurrentPage(1); // Reset to first page on search
    };

    const filteredData = (Array.isArray(compData) ? compData : []).filter(item =>
        item?.billNo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item?.zoneNm?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item?.schlNm?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const totqty = filteredData?.reduce((acc, sum) => acc + parseFloat(sum.totissqty), 0);
    const totamt = filteredData?.reduce((acc, sum) => acc + parseFloat(sum.totamt), 0);
    const totsaleqty = filteredData?.reduce((acc, sum) => acc + parseFloat(sum.totsqty), 0);
    const totbalqty = filteredData?.reduce((acc, sum) => acc + parseFloat(sum.totbqty !== null ? sum.totbqty : sum.totissqty), 0);
    const calgst = filteredData?.map((item) => {
        return {
            tot_gst:
                parseFloat(item.igst || 0) +
                parseFloat(item.cgst || 0) +
                parseFloat(item.sgst || 0)
        };
    });

    const totgst = calgst?.reduce((acc, sum) => acc + parseFloat(sum.tot_gst), 0);

    const dataFiltered = applyFilter({
        inputData: filteredData,
        comparator: getComparator(order, orderBy),
    });

    const totalPages = Math.ceil(dataFiltered.length / itemsPerPage);
    const displayedData = dataFiltered.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

    const pageNumbers = Array.from({ length: totalPages }, (_, index) => index + 1);
    const visiblePageNumbers = pageNumbers.slice(pageRange[0], pageRange[1]);

    const handleNextPageRange = () => {
        setPageRange([pageRange[0] + 5, pageRange[1] + 5]);
    };

    const handlePreviousPageRange = () => {
        setPageRange([pageRange[0] - 5, pageRange[1] - 5]);
    };

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const handleItemsPerPageChange = (event) => {
        setItemsPerPage(Number(event.target.value));
        setCurrentPage(1); // Reset to first page on change
    };
    // console.log(displayedData);

    const fields = [
        { label: 'From Date', name: 'fdt', type: 'date' },
        { label: 'To Date', name: 'tdt', type: 'date' },
        // { label: 'Barcode', name: 'freight_ch' },
    ];

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData({
            ...formData,
            [name]: value.toUpperCase()
        });
    };

    const handleDropdownChange = (e, dropdownType) => {
        const { value } = e.target;
        setFormData({
            ...formData,
            [dropdownType]: value
        });
    };


    const dropdownOrdType = [
        {
            label: 'Zone',
            id: 'zoneId',
            options: zones?.data || [],
            value: formData.zoneId,
            onChange: (newValue) => handleDropdownChange({ target: { name: 'zoneId', value: newValue ? newValue.ID : '' } }, 'zoneId'),
            getOptionLabel: (option) => option.zoneNm || '',
            isOptionEqualToValue: (option, value) => option.ID === value.ID,
            valueFinder: (options, value) => {
                if (Array.isArray(options)) {
                    return options.find(item => item.ID === value) || null;
                }
                return null;
            },
            bold: true,
        }
    ];

    const dropdownOrdType2 = [
        {
            label: 'School',
            id: 'schlId',
            options: schools?.data || [],
            value: formData.schlId,
            onChange: (newValue) => handleDropdownChange({ target: { name: 'schlId', value: newValue ? newValue.ID : '' } }, 'schlId'),
            getOptionLabel: (option) => option.schlcd || '',
            isOptionEqualToValue: (option, value) => option.ID === value.ID,
            valueFinder: (options, value) => {
                if (Array.isArray(options)) {
                    return options.find(item => item.ID === value) || null;
                }
                return null;
            },
            bold: true,
        }
    ];


    const handleSearch = (e) => {
        e.preventDefault();

        dispatch(fetchAdminSaleReport(formData))
            .unwrap()
            .then((result) => {
                // Handle success
                console.log('POST request successful!', result);
                setReportData(result);
                // alert("Admin Sale Added");
                // window.location.reload();
                // You may want to update your component state or perform any other action
            })
            .catch((error) => {
                // Handle error
                console.error('Error while making POST request:', error);
                // You may want to display an error message to the user or perform any other action
            });
    };
    return (
        <>
            <Titlebar data={adminSaleReport} />

            <div style={{ margin: '1% 0.5%' }}>
                <h2>Bill Wise Sale Report</h2>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <Paper style={{ padding: '3vh', margin: '0' }}>

                            <Grid container spacing={0.5}>
                                {fields.map((field) => (
                                    <DateInputComponent2
                                        key={field.name}
                                        formData={formData}
                                        handleDateChange={handleInputChange}
                                        handleInputChange={handleInputChange}
                                        styled={styled}
                                        inputStyles={inputStyles}
                                        isDesktop={isDesktop}
                                        field={field}
                                    />
                                ))}

                                {
                                    dropdownOrdType?.map((drop) => (
                                        <Dropdown
                                            key={drop.label}
                                            label={drop.label}
                                            id={drop.id}
                                            options={drop.options}
                                            value={drop.value}
                                            onChange={drop.onChange}
                                            isDesktop={isDesktop}
                                            getOptionLabel={drop.getOptionLabel}
                                            isOptionEqualToValue={drop.isOptionEqualToValue}
                                            valueFinder={drop.valueFinder}  // Pass `valueFinder` function here

                                        />
                                    ))
                                }

                                {
                                    dropdownOrdType2?.map((drop) => (
                                        <Dropdown
                                            key={drop.label}
                                            label={drop.label}
                                            id={drop.id}
                                            options={drop.options}
                                            value={drop.value}
                                            onChange={drop.onChange}
                                            isDesktop={isDesktop}
                                            getOptionLabel={drop.getOptionLabel}
                                            isOptionEqualToValue={drop.isOptionEqualToValue}
                                            valueFinder={drop.valueFinder}  // Pass `valueFinder` function here

                                        />
                                    ))
                                }

                                <Grid item xs={12} textAlign="center">
                                    <Button variant="contained" color="primary" onClick={handleSearch}>Search</Button>
                                </Grid>
                            </Grid>

                            <Grid item xs={12} textAlign="center" style={{ marginTop: '4%' }}>
                            </Grid>
                            <SearchTable
                                handleItemsPerPageChange={handleItemsPerPageChange}
                                searchTerm={searchTerm}
                                handleSearchChange={handleSearchChange}
                                totalItems={filteredData?.length}
                            />


                            <TableContainer style={{ marginTop: '2%', border: '1px solid #ccc' }}>
                                <Table>
                                    <TableHeader
                                        headCells={[
                                            { id: 'srNo', label: 'Sr.No' },
                                            { id: 'dt', label: 'Date' },
                                            { id: 'billNo', label: 'Bill No.' },
                                            { id: 'zoneNm', label: 'Zone' },
                                            { id: 'schlNm', label: 'School' },
                                            { id: 'schlcd', label: 'School Code' },
                                            { id: 'totissqty', label: 'Total Qty' },
                                            { id: 'totsqty', label: 'Total Sale Qty' },
                                            { id: 'totbqty', label: 'Total Bal Qty' },
                                            { id: 'totprate', label: 'Total Amt.' },
                                            { id: '', label: 'Total GST.' },
                                            { id: '', label: 'View' },
                                            // { id: 'edit', label: 'Edit' },
                                            // { id: 'delete', label: 'Delete' }
                                        ]}

                                        order={order}
                                        orderBy={orderBy}
                                        onRequestSort={handleSort}
                                        numSelected={selected.length}
                                        rowCount={compData.length}
                                        onSelectAllClick={handleSelectAllClick}
                                    />
                                    <tbody>
                                        {displayedData && displayedData.length > 0 ? (
                                            <>
                                                {displayedData.map((row) => (
                                                    <TableBody2
                                                        key={row.srNo}
                                                        id={row.srNo}
                                                        dt={row.dt}
                                                        billNo={row.billNo}
                                                        zoneId={row.zoneId}
                                                        zoneNm={row.zoneNm}
                                                        schlId={row.schlId}
                                                        schlNm={row.schlNm}
                                                        schlcd={row.schlcd}
                                                        stkId={row.stkId}
                                                        totissqty={row.totissqty}
                                                        totsqty={row.totsqty}
                                                        totbqty={row.totbqty}
                                                        totamt={row.totamt}
                                                        igst={row.igst}
                                                        cgst={row.cgst}
                                                        sgst={row.sgst}
                                                        sts={row.sts}
                                                        serialNumber={row.serialNumber}
                                                        selected={selected.indexOf(row.name) !== -1}
                                                        handleClick={(event) => handleClick(event, row.name)}
                                                    />
                                                ))}
                                                <TableBottom
                                                    totqty={totqty}
                                                    totamt={totamt}
                                                    totgst={totgst}
                                                    totbalqty={totbalqty}
                                                    totsaleqty={totsaleqty}
                                                />
                                            </>
                                        ) : (
                                            <tr>
                                                <td colSpan={12} style={{ textAlign: "center", padding: "10px" }}>
                                                    No data available
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>
                            </TableContainer>

                            <Pagination
                                currentPage={currentPage}
                                handlePageChange={handlePageChange}
                                handlePreviousPageRange={handlePreviousPageRange}
                                visiblePageNumbers={visiblePageNumbers}
                                pageNumber={pageNumbers}
                                pageRange={pageRange}
                                totalPages={totalPages}
                                handleNextPageRange={handleNextPageRange}
                            />
                        </Paper>
                    </Grid>
                </Grid>
            </div>

        </>
    );
}

export default AdminSaleReport;
